import { openDB } from "idb";
import {
  bibleBooks,
  parseScripturePassageRange,
  bibleVersion,
  bibleVersionCatalog,
  type BibleVersionId,
  type BibleVersionManifest,
} from "../shared/bible";

export { bibleBooks };

export type BibleData = string[][][];

interface InstalledBibleRecord {
  id: BibleVersionId;
  packageHash: string;
  installedAt: number;
  bytes: ArrayBuffer;
}

const DATABASE_NAME = "bible-alone-scripture-v1";
const STORE_NAME = "versions";
const cache = new Map<BibleVersionId, Promise<BibleData>>();

async function database() {
  return openDB(DATABASE_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    },
  });
}

async function sha256(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const source =
    bytes instanceof Uint8Array
      ? Uint8Array.from(bytes).buffer
      : bytes;
  const digest = await crypto.subtle.digest("SHA-256", source);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function decodeCompressedBible(bytes: ArrayBuffer): Promise<{ data: BibleData; jsonBytes: Uint8Array }> {
  const sourceBytes = new Uint8Array(bytes);
  const isGzip = sourceBytes[0] === 0x1f && sourceBytes[1] === 0x8b;
  if (isGzip && !("DecompressionStream" in window)) {
    throw new Error("This browser cannot open compressed offline Bible files.");
  }
  const jsonBytes = isGzip
    ? new Uint8Array(
        await new Response(
          new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip")),
        ).arrayBuffer(),
      )
    : sourceBytes;
  return {
    data: JSON.parse(new TextDecoder().decode(jsonBytes)) as BibleData,
    jsonBytes,
  };
}

async function loadBundled(version: BibleVersionManifest): Promise<BibleData> {
  if (!version.assetUrl) throw new Error(`${version.abbreviation} has no offline package.`);
  const response = await fetch(version.assetUrl);
  if (!response.ok) throw new Error(`The ${version.abbreviation} Bible could not be loaded.`);
  return (await decodeCompressedBible(await response.arrayBuffer())).data;
}

export async function verifyBiblePackage(
  bytes: Uint8Array,
  version: BibleVersionManifest,
): Promise<BibleData> {
  // Static asset servers may transparently decompress .gz responses. In that
  // case the exact text hash remains authoritative; compressed bytes must also
  // match the package hash when supplied.
  const compressed = bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (compressed && (await sha256(bytes)) !== version.packageHash) {
    throw new Error(`${version.abbreviation} failed its download integrity check.`);
  }
  const decoded = await decodeCompressedBible(Uint8Array.from(bytes).buffer);
  if ((await sha256(decoded.jsonBytes)) !== version.editionHash) {
    throw new Error(`${version.abbreviation} failed its text-edition integrity check.`);
  }
  return decoded.data;
}

export async function installedBibleVersions(): Promise<BibleVersionId[]> {
  const db = await database();
  const records = (await db.getAll(STORE_NAME)) as InstalledBibleRecord[];
  return ["kjv", ...records.filter((record) => record.id !== "kjv" &&
    bibleVersionCatalog.some((version) => version.id === record.id && version.packageHash === record.packageHash)
  ).map((record) => record.id)];
}

export async function installBibleVersion(
  version: BibleVersionManifest,
  onProgress?: (percent: number) => void,
): Promise<BibleData> {
  if (!version.downloadable || !version.assetUrl || !version.packageHash) {
    throw new Error(`${version.abbreviation} is not available as an offline download.`);
  }
  const response = await fetch(version.assetUrl, { cache: "no-store" });
  if (!response.ok || !response.body) {
    throw new Error(`${version.abbreviation} could not be downloaded.`);
  }
  const total = Number(response.headers.get("content-length")) || version.sizeBytes || 0;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    if (total > 0) onProgress?.(Math.min(99, Math.round((received / total) * 100)));
  }
  const bytes = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const data = await verifyBiblePackage(bytes, version);
  const db = await database();
  await db.put(STORE_NAME, {
    id: version.id,
    packageHash: version.packageHash,
    installedAt: Date.now(),
    bytes: bytes.buffer,
  } satisfies InstalledBibleRecord);
  cache.set(version.id, Promise.resolve(data));
  onProgress?.(100);
  return data;
}

export async function removeBibleVersion(id: BibleVersionId): Promise<void> {
  if (id === "kjv") return;
  const db = await database();
  await db.delete(STORE_NAME, id);
  cache.delete(id);
}

export function loadBibleVersion(id: BibleVersionId): Promise<BibleData> {
  const existing = cache.get(id);
  if (existing) return existing;
  const pending = (async () => {
    const version = bibleVersion(id);
    if (version.bundled) return loadBundled(version);
    const db = await database();
    const record = (await db.get(STORE_NAME, id)) as InstalledBibleRecord | undefined;
    if (!record || record.packageHash !== version.packageHash) {
      throw new Error(`Download ${version.abbreviation} before using it offline.`);
    }
    return (await decodeCompressedBible(record.bytes)).data;
  })().catch((error) => {
    cache.delete(id);
    throw error;
  });
  cache.set(id, pending);
  return pending;
}

export function loadKjv(): Promise<BibleData> {
  return loadBibleVersion("kjv");
}

export function formatDownloadSize(bytes: number | null): string {
  if (!bytes) return "Online";
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export { bibleVersionCatalog };

export interface ParsedReference {
  book: string;
  bookIndex: number;
  chapter: number;
  verseStart?: number;
  verseEnd?: number;
}

export function parseReference(input: string): ParsedReference | null {
  const passage = parseScripturePassageRange(input);
  if (passage && passage.start.chapter === passage.end.chapter) {
    return {
      book: passage.start.book,
      bookIndex: passage.start.bookIndex,
      chapter: passage.start.chapter,
      verseStart: passage.start.verse,
      verseEnd: passage.end.verse,
    };
  }
  const cleaned = input.trim().replace(/[–—]/g, "-");
  const match = cleaned.match(/^((?:[1-3]\s)?[A-Za-z]+(?:\s+of\s+[A-Za-z]+)?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/i);
  if (!match) return null;
  const bookIndex = bibleBooks.findIndex(
    (book) => book.toLowerCase() === match[1]?.toLowerCase(),
  );
  if (bookIndex < 0) return null;
  const chapter = Number(match[2]);
  const verseStart = match[3] ? Number(match[3]) : undefined;
  const verseEnd = match[4] ? Number(match[4]) : verseStart;
  return {
    book: bibleBooks[bookIndex]!,
    bookIndex,
    chapter,
    ...(verseStart === undefined ? {} : { verseStart }),
    ...(verseEnd === undefined ? {} : { verseEnd }),
  };
}

export function referencePath(reference: string): string {
  const parsed = parseReference(reference);
  if (!parsed) return "/scripture";
  const query = parsed.verseStart ? `?verse=${parsed.verseStart}` : "";
  return `/scripture/${encodeURIComponent(parsed.book)}/${parsed.chapter}${query}`;
}
